// KyroLabs — Practices, Products, Industries content.
// Richer copy per the latest brief. No invented statistics.
// Image tokens correspond to files under /public/assets/images/ (WebP).

// ---------- PRACTICES (6) ----------
export const PRACTICES = [
    {
        id: "customer-experience",
        name: "Customer Experience",
        short: "Omnichannel conversational AI, agent-assist and decision support.",
        long: "We design and run the CX layer end-to-end, voice and digital agents, agent-assist for humans in the loop, and the decision support that turns every conversation into actionable insights. Context-aware, always-on, native to your service systems.",
        offerings: [
            { name: "Omnichannel conversational AI", desc: "Voice and digital agents native to every customer channel." },
            { name: "Context-aware interactions", desc: "Live customer context, journey memory, and grounded responses." },
            { name: "Agent assist", desc: "Real-time nudges, summaries and next-best-action for your human teams." },
            { name: "Decision support", desc: "Insight from every conversation, fed back to the operating teams." },
        ],
        color: "#8fd0ff",
        image: "/assets/images/img_prac_cx.webp",
    },
    {
        id: "data-martech",
        name: "Data & MarTech",
        short: "Agent observability, unified data, prediction and personalised loyalty.",
        long: "We build modern data and marketing stacks that unify customer data, predict behaviour, personalise loyalty programmes, orchestrate campaigns, and provide agent observability, making the AI layer measurable end to end.",
        offerings: [
            { name: "Agent observability", desc: "Traces, evals and health for every AI agent in production." },
            { name: "Unified customer data", desc: "CDP + identity so every team sees the same customer." },
            { name: "Behaviour prediction", desc: "Propensity, churn and intent models feeding activation." },
            { name: "Personalised loyalty & campaigns", desc: "1:1 loyalty and campaign orchestration on live signal." },
        ],
        color: "#a78bfa",
        image: "/assets/images/img_prac_data.webp",
    },
    {
        id: "ai-engineering",
        name: "AI Engineering",
        short: "Custom AI, agentic frameworks, AI-native CRM/ERP and AI software engineering agents.",
        long: "Applied AI engineering across custom systems, agentic frameworks and co-created IP. We build AI-native CRM and ERP behaviours where they belong, and put AI software engineering agents inside your engineering teams.",
        offerings: [
            { name: "Custom AI systems", desc: "Bespoke agents and pipelines built on your data and processes." },
            { name: "Agentic frameworks", desc: "Multi-agent architectures, planners and tool ecosystems." },
            { name: "AI-native CRM / ERP", desc: "AI behaviours built inside Salesforce, ServiceNow and ERP." },
            { name: "AI software engineering agents", desc: "Coding assistants embedded in your engineering workflow." },
        ],
        color: "#34d399",
        image: "/assets/images/img_prac_ai.webp",
    },
    {
        id: "cybersecurity",
        name: "Cybersecurity & Resilience",
        short: "AI-led threat detection, predictive risk, compliance, business continuity.",
        long: "We secure the AI-native enterprise: AI-led threat detection, predictive risk modelling, controls for the AI layer itself, and the business continuity and disaster recovery infrastructure to keep the business running when it matters.",
        offerings: [
            { name: "AI-led threat detection", desc: "Detection and analyst copilots trained on your telemetry." },
            { name: "Predictive risk", desc: "Model risk earlier, quantify exposure, prioritise defence." },
            { name: "Compliance & controls", desc: "Policy, audit and controls that keep pace with AI regulation." },
            { name: "Business continuity & DR", desc: "Continuity design and DR programs that survive real incidents." },
        ],
        color: "#fbbf24",
        image: "/assets/images/img_prac_cyber.webp",
    },
    {
        id: "salesforce",
        name: "Salesforce Practice",
        short: "CRM implementation, managed services, integration, analytics + agentic AI.",
        long: "A full Salesforce practice, from implementation and integration to managed services, analytics and training, with agentic AI layered on top so Sales, Service and Data Cloud actually run the way your operating model needs.",
        offerings: [
            { name: "CRM implementation", desc: "Sales, Service and Data Cloud implementations, done cleanly." },
            { name: "Managed services", desc: "Steady-state platform, admin and enhancement operations." },
            { name: "Integration & analytics", desc: "Integrations to the enterprise, analytics tuned to CRM use." },
            { name: "Agentic AI on Salesforce", desc: "Agentforce and custom agents wired into your CRM flows." },
        ],
        color: "#22d3ee",
        image: "/assets/images/img_prac_salesforce.webp",
    },
    {
        id: "servicenow",
        name: "ServiceNow Practice",
        short: "ITSM · ITOM · CSM · HR · SecOps + workflow automation + agentic AI.",
        long: "A full ServiceNow practice covering ITSM, ITOM, CSM, HR and SecOps, with cross-workflow automation and agentic AI so the platform runs the way your operating model works, not the other way round.",
        offerings: [
            { name: "ITSM / ITOM / CSM", desc: "Core service, ops and customer workflow implementations." },
            { name: "HR & SecOps", desc: "HR Service Delivery and Security Operations modules end to end." },
            { name: "Workflow automation", desc: "Cross-module automation for real operating models." },
            { name: "Agentic AI on ServiceNow", desc: "Now Assist and custom agents inside your workflows." },
        ],
        color: "#f472b6",
        image: "/assets/images/img_prac_servicenow.webp",
    },
];

// ---------- PRODUCTS (7) — the Kyro suite ----------
// Each product has a richer overview AND a `subItems` list (name + short line).
export const PRODUCTS = [
    {
        id: "kyro-engage",
        name: "Kyro Engage",
        tagline: "Omnichannel engagement fabric, powered by Atlas.",
        summary:
            "Kyro Engage is the omnichannel engagement fabric: voice, WhatsApp, SMS, email and chat with one shared memory across every touchpoint. It's powered by Atlas, our Retrieval-Augmented Generation (RAG) engine, so every response is grounded in your live systems and content.",
        capabilities: [
            "Voice + every digital channel",
            "One memory across the customer journey",
            "Live handoff to human",
            "Grounded on your knowledge",
        ],
        subItems: [
            { name: "One Conversation", desc: "A single continuous conversation across every channel and every visit." },
            { name: "One Pulse", desc: "Live sentiment, intent and outcome signals from every interaction." },
            { name: "One Bridge", desc: "Warm human handoff with full context, no repetition, no drop." },
            { name: "One Assist", desc: "Agent-assist for your human teams, next best action, in real time." },
            { name: "One Insight", desc: "Voice-of-customer coding, themes and reporting out of every conversation." },
            { name: "One Flow", desc: "Journey orchestration across triggers, channels and moments that matter." },
            { name: "One Memory", desc: "A shared customer memory grounded in your CRM, order and knowledge systems." },
            { name: "One Action", desc: "Structured actions into your systems, CRM, ticketing, orders, payments." },
        ],
        color: "#8fd0ff",
        image: "/assets/images/img_prod_engage.webp",
        highlight: "One", // words with this prefix rendered in the accent colour
    },
    {
        id: "kyro-engine",
        name: "Kyro Engine",
        tagline: "The AI orchestration engine, our core IP.",
        summary:
            "Kyro Engine is the runtime that plans, routes, reasons and traces every request across your AI agents. It is foundation-model agnostic, deployable in the cloud or on-premises, and delivers the control and end-to-end observability that enterprise AI demands.",
        capabilities: [
            "Multi-agent orchestration",
            "Foundation-model agnostic",
            "Full audit & observability",
            "Cloud or on-prem deployment",
        ],
        subItems: [
            { name: "Control Tower", desc: "The single pane for every agent, run, trace and safety event across the platform." },
            { name: "Data Autopilot", desc: "Automated data pipelines and ingestion that keep agents grounded on fresh signal." },
            { name: "Cloud Command", desc: "One control layer across AWS, Google Cloud, Azure and on-prem deployments." },
        ],
        color: "#34d399",
        image: "/assets/images/img_prod_engine.webp",
    },
    {
        id: "kyro-xchange",
        name: "Kyro Xchange",
        tagline: "Data exchange & MarTech marketplace.",
        summary:
            "Kyro Xchange transforms your first-party data into a usable, revenue-generating asset, enabling discovery, productisation, activation and monetisation across your teams. Its AEO-ready intelligence layer keeps your brand visible in the age of AI-powered search.",
        capabilities: [
            "First-party data as a product",
            "AEO & SEO ready intelligence",
            "Warehouse-native activation",
            "Campaign & revenue orchestration",
        ],
        subItems: [
            { name: "Data Discovery", desc: "Find, catalog and understand the data assets you already own." },
            { name: "Data Productization", desc: "Package data into governed, reusable products with clear contracts." },
            { name: "SEO & AEO Intelligence", desc: "Content, structured data and answer-engine optimisation for the AI search era." },
            { name: "Campaign & Revenue", desc: "Warehouse-native activation for campaigns and revenue orchestration." },
        ],
        color: "#22d3ee",
        image: "/assets/images/img_prod_xchange.webp",
    },
    {
        id: "kyro-shield",
        name: "Kyro Shield",
        tagline: "AI security and resilience.",
        summary:
            "Kyro Shield is the security and resilience layer for AI-native operations: securing the AI stack itself, using AI to secure the enterprise, and keeping the business running during disruptions that would otherwise stop it.",
        capabilities: [
            "AI-first security controls",
            "Predictive risk & incident response",
            "Business continuity",
            "Disaster recovery playbooks",
        ],
        subItems: [
            { name: "AI for Security", desc: "AI-led threat detection, predictive risk and analyst copilots for the SOC." },
            { name: "Security for AI", desc: "Guardrails, red-teaming and controls specific to the AI layer, prompts, data, supply chain." },
            { name: "Business Continuity", desc: "Continuity design and rehearsal so critical operations survive incidents." },
            { name: "Disaster Recovery", desc: "RPO / RTO-aligned DR programs across cloud and on-prem systems." },
        ],
        color: "#fbbf24",
        image: "/assets/images/img_prod_shield.webp",
    },
    {
        id: "kyro-gcc",
        name: "Kyro GCC Services",
        tagline: "AI-first global capability centres, delivered.",
        summary:
            "Kyro GCC Services builds and operates an AI-first delivery centre bringing together talent, technology and governance in a single engagement. It scales your customer experience, engineering and operations with proven platforms and standards built in from day one.",
        capabilities: [
            "AI-first talent pods",
            "Standard tooling stack",
            "Delivery governance",
            "Cross-industry playbooks",
        ],
        subItems: [
            { name: "Build & Scale", desc: "Launch a new GCC with talent, technology and governance unified in one programme." },
            { name: "AI-Led Transformation", desc: "Rewire an existing GCC around AI-native ways of working and delivery." },
            { name: "Precision Talent Acquisition", desc: "Deep, targeted hiring for the AI-native roles that don't exist off the shelf." },
            { name: "Cross-Industry Expertise", desc: "Reusable playbooks across CX, data, engineering, cyber and operations." },
        ],
        color: "#f472b6",
        image: "/assets/images/img_prod_gcc.webp",
    },
    {
        id: "kyro-foundry",
        name: "Kyro Foundry",
        tagline: "Innovation foundry: build custom AI, delivered like software.",
        summary:
            "Kyro Foundry is where custom AI ideas become products. A tight discover → design → develop → deploy loop, run by the same team that builds our own IP, so your novel use cases ship as production software.",
        capabilities: [
            "Custom skill design",
            "Rapid prototyping",
            "Production-grade delivery",
            "Runs on Kyro Engine",
        ],
        subItems: [
            { name: "Discover", desc: "Frame the opportunity in days, not months, users, jobs-to-be-done, constraints and success metric, agreed in one workshop cycle." },
            { name: "Design", desc: "Design the interaction, architecture and evaluation harness before we write a line of agent code, so we know what 'good' looks like at ship." },
            { name: "Develop", desc: "Build the agent, its tools and its data pipeline on Kyro Engine, production-grade code, versioned prompts, and a full trace of every run." },
            { name: "Deploy", desc: "Roll out with observability, safety rails and human-in-the-loop from day one, then hand over to your team, or we run it for you." },
        ],
        color: "#fb7185",
        image: "/assets/images/img_prod_foundry.webp",
    },
    {
        id: "kyro-cx",
        name: "Kyro CX",
        tagline: "The AI CX suite.",
        summary:
            "Kyro CX is a pre-built suite of CX agents, running on Kyro Engine, delivered through Kyro Engage, so CX leaders can ship customer-facing AI in weeks, not months.",
        capabilities: [
            "Pre-built agent library",
            "Voice + digital in every language",
            "Analytics & QA baked in",
            "Deploys in weeks",
        ],
        subItems: [
            { name: "Personalization", desc: "1:1 personalisation across every channel, grounded in live behaviour and context." },
            { name: "Conversational AI", desc: "Enterprise-grade voice and chat agents for support, sales and service." },
            { name: "Predictive Insights", desc: "Churn, propensity and intent models feeding real-time conversation." },
            { name: "Omnichannel Orchestration", desc: "One journey across voice, WhatsApp, SMS, email, chat and web." },
        ],
        color: "#8fd0ff",
        image: "/assets/images/img_prod_cx.webp",
    },
];

// ---------- INDUSTRIES (8) — with focus areas ----------
export const INDUSTRIES_V2 = [
    {
        id: "travel",
        name: "Travel",
        short: "Booking, disruption and loyalty, voice-first, 24/7.",
        long: "For airlines, retail travel and OTAs, we run the customer conversation across booking, changes, disruption, refunds and loyalty. Grounded in live PNRs, in every language your customers speak.",
        color: "#8fd0ff",
        image: "/assets/images/img_ind_travel.webp",
        focusAreas: [
            { name: "Booking & Rebooking", desc: "End-to-end booking and rebooking on voice and chat, grounded in live PNRs." },
            { name: "Disruption Handling", desc: "Proactive outreach and rebooking during weather, ops or schedule change events." },
            { name: "Loyalty & Personalisation", desc: "Loyalty enrolment, tier servicing and personalised upsell across the journey." },
            { name: "Refunds & Servicing", desc: "Policy-grounded refund and servicing conversations, fully audited." },
        ],
    },
    {
        id: "transport",
        name: "Transport",
        short: "Rail, road and mobility, always-on customer and driver ops.",
        long: "For carriers, mobility operators and public transport, the agent handles ticketing, schedule queries, driver-side ops and disruption comms across every touchpoint.",
        color: "#a78bfa",
        image: "/assets/images/img_ind_transport.webp",
        focusAreas: [
            { name: "Ticketing & Schedule", desc: "Ticketing, schedule queries and route planning across voice and messaging." },
            { name: "Driver & Partner Ops", desc: "Driver check-ins, exception handling and dispatch coordination." },
            { name: "Disruption Comms", desc: "Real-time customer comms during service disruption or schedule changes." },
            { name: "Safety & Incident Intake", desc: "Structured intake for safety and incident reports on any channel." },
        ],
    },
    {
        id: "hospitality",
        name: "Hospitality",
        short: "Reservations, guest experience and loyalty, concierge-grade.",
        long: "For hotels, F&B and destination brands, always-on concierge coverage across reservations, in-stay service, loyalty and post-stay follow-up.",
        color: "#22d3ee",
        image: "/assets/images/img_ind_hospitality.webp",
        focusAreas: [
            { name: "Reservations", desc: "Reservations, changes and pre-arrival prep on the guest's preferred channel." },
            { name: "In-Stay Concierge", desc: "24/7 concierge for housekeeping, F&B, spa and local recommendations." },
            { name: "Smart Room Control", desc: "In-room voice control tied into PMS, F&B and housekeeping." },
            { name: "Loyalty & Follow-up", desc: "Loyalty enrolment and post-stay follow-up on every channel." },
        ],
    },
    {
        id: "logistics",
        name: "Logistics",
        short: "Supply chain, tracking and last-mile, resolved on the call.",
        long: "For 3PLs, LSPs and shippers, the agent handles supplier comms, exception routing, live tracking and last-mile customer conversations end to end.",
        color: "#34d399",
        image: "/assets/images/img_ind_logistics.webp",
        focusAreas: [
            { name: "Supplier & Partner Comms", desc: "Status, exceptions and ETAs across your supplier and 3PL network." },
            { name: "Live Tracking", desc: "Conversational access to live shipment and IoT tracking data." },
            { name: "Last-Mile", desc: "Delivery-day comms, reschedules and exception resolution with customers." },
            { name: "Warehouse Ops", desc: "Voice-first workflows for putaway, picking and cycle counts." },
        ],
    },
    {
        id: "telecom",
        name: "Telecom",
        short: "Outage, plan changes, retention and network events.",
        long: "For telcos and ISPs, outage diagnostics, plan and billing changes, retention outreach and NOC + field coordination during events.",
        color: "#fbbf24",
        image: "/assets/images/img_ind_telecom.webp",
        focusAreas: [
            { name: "Outage & Diagnostics", desc: "Live diagnostics, symptom capture and known-fix walk-through on voice." },
            { name: "Plan & Billing", desc: "Plan changes, billing queries and add-on activation, end to end." },
            { name: "Retention & Winback", desc: "Proactive save calls tied to churn models and offer catalogues." },
            { name: "Network Coordination", desc: "NOC + field coordination during network events with structured comms." },
        ],
    },
    {
        id: "healthcare",
        name: "Healthcare",
        short: "Patient care, scheduling and follow-up, compliant by design.",
        long: "For hospitals, providers and payors, patient-facing scheduling, refills, telemedicine intake, follow-up and hospital ops coordination inside strict compliance guardrails.",
        color: "#f472b6",
        image: "/assets/images/img_ind_healthcare.webp",
        focusAreas: [
            { name: "Patient Access", desc: "Scheduling, reminders, refills and follow-up on the patient's channel." },
            { name: "Telemedicine Intake", desc: "Symptom triage, coverage checks and clinician prep before the visit." },
            { name: "Hospital Ops", desc: "Bed, OT and pharmacy coordination as conversational workflows." },
            { name: "Compliance Guardrails", desc: "Policy, PII redaction and audit tuned to healthcare regulatory frameworks." },
        ],
    },
    {
        id: "bfsi",
        name: "BFSI",
        short: "Servicing, disputes, wealth and collections, securely.",
        long: "For banks, insurers, NBFCs and wealth managers, digital banking servicing, disputes intake, RM enablement, collections and fraud signal capture with policy and audit built in.",
        color: "#fb7185",
        image: "/assets/images/img_ind_bfsi.webp",
        focusAreas: [
            { name: "Digital Banking", desc: "Account, card, payment and dispute servicing on voice and chat." },
            { name: "Wealth Servicing", desc: "First-touch client servicing for RM offices, warm handoff to the RM." },
            { name: "Fraud & Risk Signals", desc: "Live fraud pattern scoring on every conversation, into the case queue." },
            { name: "Collections", desc: "Compliant, empathetic collections outreach at scale." },
        ],
    },
    {
        id: "consumer-durables",
        name: "Consumer Durables",
        short: "Dealer network, warranty and after-sales, end to end.",
        long: "For OEMs and consumer brands, dealer and partner support, warranty and AMC renewals, service scheduling and post-sales follow-up across the base.",
        color: "#c4b5fd",
        image: "/assets/images/img_ind_consumer.webp",
        focusAreas: [
            { name: "Dealer & Partner", desc: "24/7 dealer and channel support with product, warranty and order lookup." },
            { name: "Warranty & AMC", desc: "Warranty and AMC renewals across the base, on voice, in local languages." },
            { name: "After-Sales", desc: "Service scheduling, parts availability and warranty claim end to end." },
            { name: "Field Service", desc: "AI companion for field technicians during on-site interventions." },
        ],
    },
];

export function findPractice(id) {
    return PRACTICES.find((p) => p.id === id);
}
export function findProduct(id) {
    return PRODUCTS.find((p) => p.id === id);
}
export function findIndustry(id) {
    return INDUSTRIES_V2.find((i) => i.id === id);
}
